/**
 * Punkt 101 Stufe 3 — „Daraus Anfrage machen“ (Mockup c, freigegeben 26.09.2026).
 *
 * Zwei Einstiege, ein Formular: im Posteingang an der erkannten Antwort auf einen Saison-Vorschlag
 * (`messageId` → `POST /api/messages/:id/create-inquiry`) und in der Vormerkungszeile für Gäste,
 * die angerufen haben (`POST /api/inquiries`, Kanal Telefon). Alles ist vorausgefüllt und
 * änderbar; die Anfrage entsteht erst mit dem Klick (Entscheidung 2). Das Paket der Kampagne ist
 * für den Angebotsentwurf vorgewählt und abwählbar (Entscheidung 3) — den Betrag rechnet der
 * Server aus Paket × Gäste, hier steht er nur zur Anzeige. Der Einwilligungsnachweis bleibt bei
 * der Vormerkung: eine Anfrage ist keine Werbe-Einwilligung.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiMutate } from "../hooks/useApi";
import { vorgangPfad } from "../lib/pfade";
import { money } from "./format";
import { Button, Checkbox, DateInput, Drawer, Icon, Input, Textarea } from "./components";
import { anlassName } from "./SaisonKampagneDrawer";

export interface VormerkungsHinweis {
  vormerkungId: string;
  anlass: string;
  jahr: number;
  anlassDatum: string;
  sprache: string;
  paketName: string | null;
  preisProPersonCents: number | null;
}

const HERKUNFT = "font-label-md text-[10.5px] text-secondary dark:text-secondary-fixed-dim";

function Feld({ label, herkunft, icon, children }: { label: string; herkunft: string; icon: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="flex items-center justify-between gap-2 font-label-md text-[12px] font-semibold text-on-background dark:text-inverse-on-surface">
        {label}
        <span className={`inline-flex items-center gap-1 ${HERKUNFT}`}>
          <Icon name={icon} className="text-[12px]" /> {herkunft}
        </span>
      </span>
      {children}
    </label>
  );
}

function Schritt({ n, titel, children }: { n: number; titel: string; children: React.ReactNode }) {
  return (
    <section className="flex flex-col gap-3 border-t border-[#F2ECDF] pt-4 first:border-t-0 first:pt-0 dark:border-white/[0.06]">
      <h5 className="flex items-center gap-2 font-label-md text-[13px] font-bold text-on-background dark:text-inverse-on-surface">
        <span className="inline-flex size-5 items-center justify-center rounded-full bg-primary/10 text-[11px] text-primary">{n}</span>
        {titel}
      </h5>
      {children}
    </section>
  );
}

export function AnfrageAusVormerkungDrawer({
  vormerkung,
  vorbelegung,
  messageId,
  onClose,
}: {
  vormerkung: VormerkungsHinweis;
  vorbelegung: { name: string; email: string; nachricht: string };
  /** Gesetzt = aus der Antwort im Posteingang; sonst Anruf (Vormerkungszeile). */
  messageId?: string;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const [name, setName] = useState(vorbelegung.name);
  const [email, setEmail] = useState(vorbelegung.email);
  const [anlass, setAnlass] = useState(anlassName(vormerkung.anlass));
  const [datum, setDatum] = useState(vormerkung.anlassDatum);
  const [gaeste, setGaeste] = useState("");
  const [nachricht, setNachricht] = useState(vorbelegung.nachricht);
  const [paket, setPaket] = useState(Boolean(vormerkung.paketName));
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const englisch = vormerkung.sprache.toLowerCase().startsWith("en");
  const quelle = messageId ? { label: "Antwort", icon: "mail" } : { label: "Anruf", icon: "call" };

  async function anlegen() {
    if (!name.trim()) return;
    setLaeuft(true);
    setFehler(null);
    try {
      const body: Record<string, unknown> = {
        customerName: name.trim(),
        occasion: anlass.trim() || undefined,
        vormerkungId: vormerkung.vormerkungId,
        vormerkungPaket: paket,
      };
      if (email.trim()) body.customerEmail = email.trim();
      if (datum) body.eventDate = new Date(datum).toISOString();
      if (gaeste) body.guests = Number(gaeste);
      if (nachricht.trim()) body.message = nachricht.trim();
      const eventId = messageId
        ? (await apiMutate<{ eventId: string }>(`/api/messages/${messageId}/create-inquiry`, "POST", body)).eventId
        : (await apiMutate<{ generatedEventId: string }>("/api/inquiries", "POST", { ...body, kanal: "phone" })).generatedEventId;
      navigate(vorgangPfad(eventId));
    } catch (e) {
      setFehler(e instanceof Error ? e.message : "Anfrage konnte nicht angelegt werden.");
      setLaeuft(false);
    }
  }

  return (
    <Drawer
      open
      onClose={onClose}
      title={
        <span className="flex items-center gap-2">
          <Icon name="add_task" className="text-[20px] text-primary" /> Daraus Anfrage machen
        </span>
      }
      subtitle={`${messageId ? `Aus der Antwort auf den ${anlassName(vormerkung.anlass)}-Vorschlag.` : `Für den Anruf eines Gastes mit ${anlassName(vormerkung.anlass)}-Vormerkung.`} Alles ist vorausgefüllt und lässt sich ändern.`}
      footer={
        <div className="flex w-full flex-wrap items-center gap-2">
          <Button variant="secondary" onClick={onClose} disabled={laeuft}>
            Abbrechen
          </Button>
          <Button className="ml-auto" onClick={() => void anlegen()} disabled={laeuft || !name.trim()}>
            <Icon name="check" className="text-[16px]" /> {laeuft ? "Wird angelegt…" : "Anfrage anlegen"}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4" data-testid="anfrage-aus-vormerkung">
        <Schritt n={1} titel="Übernommene Angaben">
          <Feld label="Name" herkunft={messageId ? "Absenderzeile" : "bitte eintragen"} icon={messageId ? "mail" : "edit"}>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name der Kundin/des Kunden" />
          </Feld>
          <Feld label="E-Mail" herkunft="Vormerkung" icon="event_upcoming">
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </Feld>
          <div className="grid gap-3 sm:grid-cols-2">
            <Feld label="Anlass" herkunft="Vormerkung" icon="event_upcoming">
              <Input value={anlass} onChange={(e) => setAnlass(e.target.value)} className="min-w-0" />
            </Feld>
            <Feld label="Datum" herkunft="Vormerkung" icon="event_upcoming">
              <DateInput value={datum} onValueChange={setDatum} className="min-w-0" />
            </Feld>
            <Feld label="Gäste" herkunft="bitte prüfen" icon="edit">
              <Input type="number" min={1} value={gaeste} onChange={(e) => setGaeste(e.target.value)} placeholder="z. B. 4" className="min-w-0" />
            </Feld>
            <Feld label="Sprache" herkunft="Vormerkung" icon="event_upcoming">
              <Input value={englisch ? "Englisch" : "Deutsch"} readOnly className="min-w-0" />
            </Feld>
          </div>
          <Feld label="Nachricht" herkunft={quelle.label} icon={quelle.icon}>
            <Textarea rows={4} value={nachricht} onChange={(e) => setNachricht(e.target.value)} placeholder={messageId ? "" : "Was hat der Gast am Telefon gesagt?"} />
          </Feld>
        </Schritt>

        <Schritt n={2} titel="Paket für den Angebotsentwurf">
          {vormerkung.paketName ? (
            <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-outline-variant/40 p-3 dark:border-outline/30">
              <Checkbox checked={paket} onChange={(e) => setPaket(e.target.checked)} className="mt-1" />
              <span className="min-w-0 flex-1">
                <span className="flex flex-wrap items-baseline justify-between gap-2">
                  <span className="font-body-sm text-[14px] font-semibold text-on-background dark:text-inverse-on-surface">{vormerkung.paketName}</span>
                  {vormerkung.preisProPersonCents != null && (
                    <span className="font-body-sm text-[13px] font-semibold tabular-nums text-on-background dark:text-inverse-on-surface">
                      {money(vormerkung.preisProPersonCents)} p. P.
                    </span>
                  )}
                </span>
                <span className="mt-0.5 block font-body-sm text-[12px] text-secondary dark:text-secondary-fixed-dim">
                  Aus der Saison-Kampagne {anlassName(vormerkung.anlass)} {vormerkung.jahr}. MAESTRO wählt es im automatischen Entwurf vor.
                </span>
                <span className="mt-1 block font-body-sm text-[11.5px] text-secondary dark:text-secondary-fixed-dim">
                  Den Betrag rechnet der Server aus Paket × Gäste. Die KI setzt keine Preise.
                </span>
              </span>
            </label>
          ) : (
            <p className="font-body-sm text-[12.5px] text-secondary dark:text-secondary-fixed-dim">Die Kampagne hat kein Paket mehr — der Entwurf entsteht ohne Vorauswahl.</p>
          )}
        </Schritt>

        <Schritt n={3} titel="Was beim Klick passiert">
          <p className="flex items-start gap-2 font-body-sm text-[12.5px] text-on-surface-variant">
            <Icon name="task_alt" className="mt-0.5 text-[16px] text-tertiary" />
            <span>
              Eine normale Anfrage entsteht, wie bei jeder Posteingangs-Mail, und MAESTRO bereitet den Angebotsentwurf vor. Die Anfrage ist mit der Vormerkung{" "}
              {anlassName(vormerkung.anlass)} {vormerkung.jahr} verknüpft und zählt in der Kennzahl „Vormerkung → Buchung“.
            </span>
          </p>
          <p className="flex items-start gap-2 font-body-sm text-[12.5px] text-on-surface-variant">
            <Icon name="shield" className="mt-0.5 text-[16px] text-secondary" />
            <span>
              Nicht übernommen: der Einwilligungsnachweis. Der bleibt bei der Vormerkung und gilt weiter nur für {anlassName(vormerkung.anlass)}-Infos.
            </span>
          </p>
        </Schritt>
        {fehler && <p className="font-body-sm text-[12px] text-error">{fehler}</p>}
      </div>
    </Drawer>
  );
}
